import { ReactNode } from "react";
import type { RouterOutputs } from "@/lib/trpc";
import { YourWeekReflectionCard } from "./YourWeekReflectionCard";

type View = RouterOutputs["yourWeek"]["forWeek"];

/**
 * YOUR WEEK — the FROZEN seven-section hierarchy (D7).
 *
 *   1 Header (week selector · private framing · workspace filter)
 *   2 Your week by day
 *   3 Blockers
 *   4 Your priorities
 *   5 Carry forward
 *   6 Team Direction context
 *   7 Open Weekly Receipt
 *   8 Reflect on your week (Stage B — AI reflection, LAST and subordinate)
 *
 * Section 1's week selector and private label live in the page's scaffold
 * header; the workspace filter is rendered here as the first block so the
 * order the reader sees is exactly the list above.
 *
 * Sections 1-7 are purely presentational. Section 8 mounts the Stage B
 * reflection card, which owns its own mutation and its own ephemeral state —
 * this file still performs no query, no mutation and no disclosure control of
 * its own, and the reflection is deliberately LAST so every section the owner
 * declared or captured is read before anything a model wrote. There is
 * deliberately NO Accomplishments section (the day layer already carries
 * highlights and shipped) and no summary — Stage A1 makes no judgement about
 * the owner's week.
 */
export const YOUR_WEEK_SECTION_ORDER = [
  "workspace-filter",
  "by-day",
  "blockers",
  "priorities",
  "carry-forward",
  "direction-context",
  "weekly-receipt",
  "reflection",
] as const;

function Card({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <section
      data-testid={testId}
      className="rounded-3xl border border-border/60 bg-card p-4 shadow-sm"
    >
      {children}
    </section>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return <h2 className="mb-2 text-sm font-semibold text-foreground">{children}</h2>;
}

export function YourWeekSections({
  data,
  isAr,
  weekStartKey,
  filterTeamId,
  onFilterChange,
  workspaces,
  dayName,
  ReceiptLink,
}: {
  data: View;
  isAr: boolean;
  weekStartKey: string;
  filterTeamId: number | null;
  onFilterChange: (id: number | null) => void;
  workspaces: Array<{ id: number; name: string; isPersonal: boolean }>;
  dayName: (dateKey: string) => string;
  ReceiptLink: (props: { children: ReactNode }) => ReactNode;
}) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const importanceLabel = (i: "high" | "medium" | "low") =>
    i === "high" ? t("High", "عالية") : i === "low" ? t("Low", "منخفضة") : t("Medium", "متوسطة");
  const statusLabel = (s: View["priorities"][number]["status"]) =>
    s === "done"
      ? t("Done", "منجزة")
      : s === "in_progress"
        ? t("In progress", "قيد التنفيذ")
        : s === "dropped"
          ? t("Dropped", "متروكة")
          : t("Not started", "لم تبدأ");

  const daysWithContent = data.days.filter(d => d.source !== "none");
  const started = data.weeklyReceipt.filter(w => w.state !== "none");

  return (
    <div className="space-y-2.5">
      {/* 1 — workspace filter (header block) */}
      <Card testId="yourweek-workspace-filter">
        <Heading>{t("Showing", "المعروض")}</Heading>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onFilterChange(null)}
            aria-pressed={filterTeamId === null}
            className={`rounded-full border px-3 py-1 text-xs ${
              filterTeamId === null ? "border-coral bg-coral-soft/60 text-foreground" : "border-border/60"
            }`}
          >
            {t("All my work", "كل أعمالي")}
          </button>
          {workspaces.map(w => (
            <button
              key={w.id}
              type="button"
              onClick={() => onFilterChange(w.id)}
              aria-pressed={filterTeamId === w.id}
              className={`rounded-full border px-3 py-1 text-xs ${
                filterTeamId === w.id ? "border-coral bg-coral-soft/60 text-foreground" : "border-border/60"
              }`}
            >
              {w.name}
            </button>
          ))}
        </div>
      </Card>

      {/* 2 — your week by day */}
      {daysWithContent.length > 0 ? (
        <Card testId="yourweek-by-day">
          <Heading>{t("Your week by day", "أسبوعك يومًا بيوم")}</Heading>
          <ul className="space-y-3">
            {daysWithContent.map(d => (
              <li key={d.dayKey} data-testid={`yourweek-day-${d.dayKey}`}>
                <div className="text-xs font-medium text-muted-foreground">{dayName(d.dayKey)}</div>
                {d.source === "receipt" ? (
                  <div className="mt-1">
                    {d.headline ? <div className="text-sm font-medium">{d.headline}</div> : null}
                    <ul className="mt-1 list-disc space-y-0.5 ps-5 text-sm text-muted-foreground">
                      {d.highlights.map((h, i) => <li key={`h${i}`}>{h}</li>)}
                      {d.shipped.map((s, i) => <li key={`s${i}`}>{s}</li>)}
                    </ul>
                  </div>
                ) : (
                  <ul className="mt-1 list-disc space-y-0.5 ps-5 text-sm text-muted-foreground">
                    {d.captures.map((c, i) => <li key={i}>{c.title}</li>)}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* 3 — blockers */}
      {data.blockers.length > 0 ? (
        <Card testId="yourweek-blockers">
          <Heading>{t("Blockers", "العوائق")}</Heading>
          <ul className="list-disc space-y-1 ps-5 text-sm text-muted-foreground">
            {data.blockers.map((b, i) => (
              <li key={i}>
                {b.text}
                <span className="ms-2 text-xs opacity-70">{dayName(b.dayKey)}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* 4 — your priorities */}
      {data.priorities.length > 0 ? (
        <Card testId="yourweek-priorities">
          <Heading>{t("Your priorities", "أولوياتك")}</Heading>
          {/* Personal priorities and their direction links carry no workspace
              column, so they are the owner's whole week regardless of the
              filter. Say so plainly when a filter is active, rather than let
              the selected workspace imply ownership. Copy only — no filtering
              logic, no counts. */}
          {filterTeamId !== null ? (
            <p className="mb-2 text-xs text-muted-foreground" data-testid="yourweek-priorities-scope-note">
              {t("Personal priorities are shown across your work.", "تُعرض أولوياتك الشخصية عبر كل أعمالك.")}
            </p>
          ) : null}
          <ul className="space-y-2.5">
            {data.priorities.map((p, i) => (
              <li key={i} className="text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{p.title}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                    {statusLabel(p.status)}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{importanceLabel(p.importance)}</span>
                  {/* Additive only — absence is never rendered as a negative. */}
                  {p.completedThisWeek ? (
                    <span
                      data-testid="yourweek-completed-marker"
                      className="rounded-full bg-coral-soft/70 px-2 py-0.5 text-[11px]"
                    >
                      {t("Completed this week", "أُنجزت هذا الأسبوع")}
                    </span>
                  ) : null}
                  {p.carriedForward ? (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                      {t("Carried forward", "مُرحّلة")}
                    </span>
                  ) : null}
                </div>
                {p.description ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">{p.description}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* 5 — carry forward */}
      {data.carryOver.length > 0 ? (
        <Card testId="yourweek-carry-forward">
          <Heading>{t("Carry forward", "المُرحّل")}</Heading>
          <ul className="list-disc space-y-1 ps-5 text-sm text-muted-foreground">
            {data.carryOver.map((c, i) => <li key={i}>{c.title}</li>)}
          </ul>
        </Card>
      ) : null}

      {/* 6 — team direction context */}
      <Card testId="yourweek-direction-context">
        <Heading>{t("Team direction context", "سياق اتجاه الفريق")}</Heading>
        {!data.directionContextAvailable ? (
          <p className="text-sm text-muted-foreground" data-testid="yourweek-direction-degraded">
            {t("Couldn’t load team direction context.", "تعذّر تحميل سياق اتجاه الفريق.")}
          </p>
        ) : data.priorities.some(p => p.directions.length > 0) ? (
          <ul className="space-y-1.5 text-sm">
            {data.priorities
              .filter(p => p.directions.length > 0)
              .map((p, i) => (
                <li key={i}>
                  <span className="text-muted-foreground">{p.title}</span>
                  <ul className="mt-0.5 list-disc space-y-0.5 ps-5">
                    {p.directions.map((d, j) => (
                      <li key={j}>
                        {d.title}
                        {d.archived ? (
                          <span
                            data-testid="yourweek-archived-direction"
                            className="ms-2 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                          >
                            {t("Archived", "مؤرشف")}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("Not connected to a team direction.", "غير مرتبطة باتجاه فريق.")}
          </p>
        )}
      </Card>

      {/* 7 — open weekly receipt (navigation only) */}
      <Card testId="yourweek-weekly-receipt">
        <Heading>{t("Weekly Receipt", "الملخّص الأسبوعي")}</Heading>
        {/* NAVIGATIONAL ONLY. The weekly receipt's disclosure lifecycle belongs
            to the weekly receipt surface, and the published Stage A1 client
            contract forbids that vocabulary here. Your Week therefore names
            only WHERE a weekly receipt exists this week, never its state. */}
        {started.length > 0 ? (
          <ul className="mb-2 space-y-1 text-sm text-muted-foreground">
            {started.map((w, i) => (
              <li key={i}>{w.workspaceLabel ?? t("Started this week", "بدأ هذا الأسبوع")}</li>
            ))}
          </ul>
        ) : (
          <p className="mb-2 text-sm text-muted-foreground">
            {t("Not started yet.", "لم يبدأ بعد.")}
          </p>
        )}
        <ReceiptLink>
          <span className="inline-flex rounded-full border border-border/60 px-3 py-1 text-xs hover:bg-accent/50">
            {t("Open Weekly Receipt", "افتح الملخّص الأسبوعي")}
          </span>
        </ReceiptLink>
      </Card>

      {/* 8 — Stage B reflection. LAST by design: subordinate to everything the
          owner declared or captured above, and the only section that talks to a
          model. It runs nothing until the owner clicks. */}
      <Card testId="yourweek-reflection">
        <Heading>{t("Reflect on your week", "تأمّل أسبوعك")}</Heading>
        <YourWeekReflectionCard
          weekStartKey={weekStartKey}
          workspaceTeamId={filterTeamId}
          isAr={isAr}
        />
      </Card>
    </div>
  );
}
